import { Sparkles, Palette, Frame, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

const AboutPage = () => {
  return (
    <div data-testid="about-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-copper text-sm tracking-[0.3em] uppercase mb-4 font-body">My Story</p>
          <h1 
            data-testid="about-title"
            className="font-heading text-4xl md:text-6xl mb-6"
          >
            About <span className="copper-text">Kairos</span>
          </h1>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/5] border border-white/10 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1643968704781-df3b260df6a7?w=600&fit=crop"
                alt="Copper art workspace"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative Frame */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-copper/50" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-copper/50" />
          </div>

          {/* Story */}
          <div className="flex flex-col justify-center">
            <h2 className="font-heading text-3xl mb-6 copper-text">The Artist</h2>
            <div className="space-y-6 font-body text-gray-300 leading-relaxed">
              <p data-testid="about-story">
                Hello! This copper art business was a beautiful addition to my hobbies by taking copper wire, cutting, flattening, glueing, and painting onto a canvas that can be framed and hung on a wall or placed on a shelf!
              </p>
              <p>
                I never thought of myself as an artist or even had the skills to get into art, until a few months ago where I just from boredom at work started messing with random pieces of copper and making them into simple shapes and designs on a table and thought about how it could be better.
              </p>
              <p>
                4-5 months later I have now created a beautiful copper mosaic art form that I don't think has been done before! It has been so much fun making all these pieces and I cannot wait to make more pieces down the road!
              </p>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="border-t border-white/5 pt-24">
          <div className="text-center mb-16">
            <h2 
              data-testid="process-title"
              className="font-heading text-3xl md:text-4xl mb-4"
            >
              The <span className="copper-text">Process</span>
            </h2>
            <p className="font-body text-gray-400 max-w-2xl mx-auto">
              Every piece goes through a meticulous handcrafting process to transform your images into stunning copper mosaic art.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 */}
            <div 
              data-testid="process-step-1"
              className="bg-white/[0.02] border border-white/5 p-6 hover:border-copper/30 transition-all duration-500 group"
            >
              <div className="w-12 h-12 mb-4 flex items-center justify-center border border-copper/50 group-hover:border-copper transition-colors">
                <span className="font-heading text-copper text-xl">01</span>
              </div>
              <h3 className="font-heading text-lg mb-2 text-white">Cut</h3>
              <p className="font-body text-gray-400 text-sm">
                Copper wire is carefully cut into individual pieces of varying sizes.
              </p>
            </div>

            {/* Step 2 */}
            <div 
              data-testid="process-step-2"
              className="bg-white/[0.02] border border-white/5 p-6 hover:border-copper/30 transition-all duration-500 group"
            >
              <div className="w-12 h-12 mb-4 flex items-center justify-center border border-copper/50 group-hover:border-copper transition-colors">
                <span className="font-heading text-copper text-xl">02</span>
              </div>
              <h3 className="font-heading text-lg mb-2 text-white">Flatten</h3>
              <p className="font-body text-gray-400 text-sm">
                Each piece is flattened to create the perfect surface for the mosaic.
              </p>
            </div>

            {/* Step 3 */}
            <div 
              data-testid="process-step-3"
              className="bg-white/[0.02] border border-white/5 p-6 hover:border-copper/30 transition-all duration-500 group"
            >
              <div className="w-12 h-12 mb-4 flex items-center justify-center border border-copper/50 group-hover:border-copper transition-colors">
                <span className="font-heading text-copper text-xl">03</span>
              </div>
              <h3 className="font-heading text-lg mb-2 text-white">Glue</h3>
              <p className="font-body text-gray-400 text-sm">
                Pieces are meticulously glued onto the canvas following the design.
              </p>
            </div>

            {/* Step 4 */}
            <div 
              data-testid="process-step-4"
              className="bg-white/[0.02] border border-white/5 p-6 hover:border-copper/30 transition-all duration-500 group"
            >
              <div className="w-12 h-12 mb-4 flex items-center justify-center border border-copper/50 group-hover:border-copper transition-colors">
                <span className="font-heading text-copper text-xl">04</span>
              </div>
              <h3 className="font-heading text-lg mb-2 text-white">Paint</h3>
              <p className="font-body text-gray-400 text-sm">
                Final touches with paint bring colors and depth to the piece.
              </p>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="border-t border-white/5 pt-24 mt-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div data-testid="value-handcrafted" className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 border border-copper/50 flex items-center justify-center">
                <Sparkles className="text-copper" size={28} />
              </div>
              <h3 className="font-heading text-xl mb-2 text-white">100% Handcrafted</h3>
              <p className="font-body text-gray-400 text-sm">
                Every piece is made entirely by hand, no machines involved.
              </p>
            </div>

            <div data-testid="value-unique" className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 border border-copper/50 flex items-center justify-center">
                <Palette className="text-copper" size={28} />
              </div>
              <h3 className="font-heading text-xl mb-2 text-white">Unique Medium</h3>
              <p className="font-body text-gray-400 text-sm">
                A revolutionary art form you won't find anywhere else.
              </p>
            </div>

            <div data-testid="value-passion" className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 border border-copper/50 flex items-center justify-center">
                <Heart className="text-copper" size={28} />
              </div>
              <h3 className="font-heading text-xl mb-2 text-white">Made with Love</h3>
              <p className="font-body text-gray-400 text-sm">
                Every piece is created with passion and attention to detail.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-24 pt-16 border-t border-white/5">
          <h2 className="font-heading text-2xl md:text-3xl mb-6">
            Ready to Create Your <span className="copper-text">Masterpiece</span>?
          </h2>
          <Link to="/quote">
            <Button
              data-testid="about-cta-btn"
              className="bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase px-8 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(184,115,51,0.4)]"
            >
              Get Your Quote
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
